import { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, Plus, Loader2 } from 'lucide-react';
import { useToast } from '../ui/Toast';
import type { UnmatchedLohnart, UnmatchedCostCenter } from '../../types/lohnBuchungsbeleg';
import { fmtMoney, lohnBuchungsbelegApi } from '../../services/lohnBuchungsbelegApi';
import type { PrefillLine } from './VorlageTab';

interface Props {
    unmatched: UnmatchedLohnart[];
    total: number;
    unmatchedCostCenters?: UnmatchedCostCenter[];
    unmatchedCostCenterTotal?: number;
    /** Only show the "create template line" action when the run is editable. */
    canEdit?: boolean;
    /** Feature 2: open the Vorlage editor prefilled from an unmatched Lohnart. */
    onCreateTemplateLine?: (prefill: PrefillLine) => void;
    /** Called after a missing cost center is created inline, so the voucher can recompute. */
    onCostCenterCreated?: () => void;
}

/** Build a template-line draft from an unmatched Lohnart row (Feature 2). */
function prefillFrom(u: UnmatchedLohnart): PrefillLine {
    return {
        sortOrder: 0, // VorlageTab appends after the last line
        sollAccount: u.soll || null,
        habenAccount: u.haben || null,
        beschreibung: u.lohnart,
        matchMode: 'SollHabenLohnart',
        sign: 1,
        splitByCostCenter: true,
        targetCostCenter: null,
        textLabel: '',
        includeInChecksum: true,
    };
}

/**
 * Diagnostics shown above the grid so residual differences aren't missed:
 *  1. Raw rows that matched no template line (a Lohnart the template doesn't cover) —
 *     each with a "Vorlage-Zeile anlegen" shortcut that prefills the template editor.
 *  2. Cost centers used in the Rohdaten that have no column — those amounts are silently
 *     dropped by the matrix; add the column in "Vorlage / Einstellungen" to capture them.
 */
export function UnmatchedPanel({ unmatched, total, unmatchedCostCenters = [], unmatchedCostCenterTotal = 0, canEdit, onCreateTemplateLine, onCostCenterCreated }: Props) {
    const { toast } = useToast();
    const [openLa, setOpenLa] = useState(true);
    const [openCc, setOpenCc] = useState(true);
    const [creatingCc, setCreatingCc] = useState<string | null>(null);
    const hasLa = unmatched.length > 0;
    const hasCc = unmatchedCostCenters.length > 0;
    if (!hasLa && !hasCc) return null;

    const createCostCenter = async (code: string) => {
        setCreatingCc(code);
        try {
            await lohnBuchungsbelegApi.createCostCenter({ costCenterCode: code, columnLabel: `KST ${code}`, sortOrder: 9999 });
            toast(`Kostenstelle ${code} angelegt`, 'success');
            onCostCenterCreated?.();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen', 'error');
        } finally { setCreatingCc(null); }
    };

    return (
        <div className="mt-4 space-y-3">
            {hasLa && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/60">
                    <button
                        onClick={() => setOpenLa(o => !o)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-800"
                    >
                        {openLa ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <AlertTriangle className="h-4 w-4" />
                        <span>{unmatched.length} nicht zugeordnete Lohnart{unmatched.length === 1 ? '' : 'en'}</span>
                        <span className="ml-1 font-mono text-xs text-amber-600">(Summe {fmtMoney(total, false)})</span>
                        <span className="ml-auto text-xs font-normal text-amber-500">nicht im Beleg enthalten</span>
                    </button>
                    {openLa && (
                        <div className="border-t border-amber-200 px-3 py-2">
                            <p className="mb-2 text-xs text-amber-700">
                                Diese Rohdaten-Zeilen werden von keiner Vorlage-Zeile erfasst und fehlen daher im Beleg.
                                {canEdit && onCreateTemplateLine
                                    ? ' Lege pro Lohnart mit einem Klick eine passende Vorlage-Zeile an.'
                                    : ' Lege dafür im Tab «Vorlage / Einstellungen» eine Buchungszeile mit der passenden Lohnart-Nummer an.'}
                            </p>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-xs">
                                    <thead className="text-amber-600">
                                        <tr>
                                            <th className="px-2 py-1 text-left font-semibold">Lohnart</th>
                                            <th className="px-2 py-1 text-right font-semibold">Soll</th>
                                            <th className="px-2 py-1 text-right font-semibold">Haben</th>
                                            <th className="px-2 py-1 text-right font-semibold">Zeilen</th>
                                            <th className="px-2 py-1 text-right font-semibold">Betrag</th>
                                            {canEdit && onCreateTemplateLine && <th className="px-2 py-1" />}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unmatched.map((u, i) => (
                                            <tr key={i} className="border-t border-amber-100 text-amber-900">
                                                <td className="px-2 py-1">{u.lohnart || '(ohne Lohnart)'}</td>
                                                <td className="px-2 py-1 text-right font-mono text-amber-600">{u.soll}</td>
                                                <td className="px-2 py-1 text-right font-mono text-amber-600">{u.haben}</td>
                                                <td className="px-2 py-1 text-right font-mono">{u.count}</td>
                                                <td className="px-2 py-1 text-right font-mono font-semibold">{fmtMoney(u.sum, false)}</td>
                                                {canEdit && onCreateTemplateLine && (
                                                    <td className="px-2 py-1 text-right">
                                                        <button
                                                            onClick={() => onCreateTemplateLine(prefillFrom(u))}
                                                            className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-700 hover:bg-amber-100"
                                                        >
                                                            <Plus className="h-3 w-3" /> Vorlage-Zeile anlegen
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {hasCc && (
                <div className="rounded-lg border border-rose-200 bg-rose-50/60">
                    <button
                        onClick={() => setOpenCc(o => !o)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-rose-800"
                    >
                        {openCc ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <AlertTriangle className="h-4 w-4" />
                        <span>{unmatchedCostCenters.length} nicht abgedeckte Kostenstelle{unmatchedCostCenters.length === 1 ? '' : 'n'}</span>
                        <span className="ml-1 font-mono text-xs text-rose-600">(Summe {fmtMoney(unmatchedCostCenterTotal, false)})</span>
                        <span className="ml-auto text-xs font-normal text-rose-500">Beträge fallen weg</span>
                    </button>
                    {openCc && (
                        <div className="border-t border-rose-200 px-3 py-2">
                            <p className="mb-2 text-xs text-rose-700">
                                Diese Kostenstellen kommen in den Rohdaten vor, sind aber keine Spalte im Beleg. Ihre Beträge
                                werden daher <strong>stillschweigend weggelassen</strong>. Lege sie im Tab «Vorlage / Einstellungen»
                                als Kostenstellen-Spalte an, damit sie erfasst werden.
                            </p>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-xs">
                                    <thead className="text-rose-600">
                                        <tr>
                                            <th className="px-2 py-1 text-left font-semibold">Kostenstelle</th>
                                            <th className="px-2 py-1 text-right font-semibold">Vorkommen</th>
                                            <th className="px-2 py-1 text-right font-semibold">Betrag (netto)</th>
                                            {canEdit && onCostCenterCreated && <th className="px-2 py-1" />}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unmatchedCostCenters.map((c, i) => (
                                            <tr key={i} className="border-t border-rose-100 text-rose-900">
                                                <td className="px-2 py-1 font-mono">{c.costCenter}</td>
                                                <td className="px-2 py-1 text-right font-mono">{c.count}</td>
                                                <td className="px-2 py-1 text-right font-mono font-semibold">{fmtMoney(c.sum, false)}</td>
                                                {canEdit && onCostCenterCreated && (
                                                    <td className="px-2 py-1 text-right">
                                                        <button
                                                            onClick={() => createCostCenter(c.costCenter)}
                                                            disabled={creatingCc !== null}
                                                            className="inline-flex items-center gap-1 rounded border border-rose-300 bg-white px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                                                        >
                                                            {creatingCc === c.costCenter ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Kostenstelle anlegen
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
